import React, { useState, useMemo } from 'react';
import { CARD_GAMES, getGameSlug } from '../constants/games';

export interface GameFilterConfig {
  quickOptions: Record<string, string[]>;
  advanced: string[];
}

/**
 * Configurações base por jogo. Cada jogo é definido apenas UMA vez pela sua slug canônica.
 * IDs numéricos e apelidos são vinculados automaticamente sem duplicação de dados.
 */
const BASE_GAME_FILTERS: Record<string, GameFilterConfig> = {
  "digimon": {
    quickOptions: { 
      cardType: ['Digi-Egg','Digimon','Tamer','Option'],
      form:     ['In-Training','Rookie','Champion','Ultimate','Mega'],
      attribute:['Data','Vaccine','Virus','Free'],
      level:    ["Lv.2","Lv.3","Lv.4","Lv.5","Lv.6","Lv.7"] 
    },
    advanced: ["id","code","name","level","colors","cardType","form","attribute","type","dp","playCost","digivolveCost","effect"]
  },
  "dragon-ball-fusion": {
    quickOptions: {
      cardType:['LEADER','BATTLE','EXTRA','ENERGY MAKER'],
      color:   ['Red','Blue','Green','Yellow','Black'],
      rarity:  ['L','C','U','R','SR','SCR','PR']
    },
    advanced: ["id","code","name","rarity","color","cardType","cost","specifiedCost","power","comboPower","features","effect"]
  },
  "fab": {
    quickOptions: {
      color: ['Red', 'Blue', 'Yellow'],
      cardType: [
        "Action",
        "Attack Reaction",
        "Block",
        "Companion",
        "Defense Reaction",
        "Demi-Hero",
        "Equipment",
        "Hero",
        "Instant",
        "Macro",
        "Mentor",
        "Resource",
        "Token",
        "Weapon"
      ],
      class: [
        "Adjudicator",
        "Assassin",
        "Bard",
        "Brute",
        "Generic",
        "Guardian",
        "Illusionist",
        "Mechanologist",
        "Merchant",
        "Necromancer",
        "Ninja",
        "Pirate",
        "Ranger",
        "Runeblade",
        "Shapeshifter",
        "Thief",
        "Warrior",
        "Wizard"
      ],
      talent: [
        "Chaos",
        "Draconic",
        "Earth",
        "Elemental",
        "Ice",
        "Light",
        "Lightning",
        "Mystic",
        "Revered",
        "Reviled",
        "Royal",
        "Shadow"
      ],
      subtype: [
        "(1H)",
        "(2H)",
        "Affliction",
        "Ally",
        "Arrow",
        "Ash",
        "Attack",
        "Aura",
        "Construct",
        "Figment",
        "Invocation",
        "Item",
        "Landmark",
        "Off-Hand",
        "Quiver"
      ]
    },
    advanced: ["id", "name", "type", "rarity", "health", "arcane", "pitch", "cost", "power", "defense"]
  },
  "gundam": {
    quickOptions: { 
      cardType: ['UNIT', 'PILOT', 'COMMAND', 'BASE', 'RESOURCE'],
      color:    ['Blue', 'Green', 'Red', 'White', 'Purple'],
      rarity:   ['C', 'U', 'R', 'LR', 'P']
    },
    advanced: ["id", "code", "name", "cardType", "color", "rarity", "cost", "level", "ap", "hp", "zone", "trait", "link", "source_title"]
  },
  "universus": {
    quickOptions: { 
      type:   ['Character', 'Attack', 'Foundation', 'Action', 'Asset'],
      icons:  ['Air', 'Chaos', 'Death', 'Earth', 'Evil', 'Fire', 'Good', 'Infinity', 'Life', 'Order', 'Void', 'Water', 'All'],
      rarity: ['Common', 'Uncommon', 'Rare', 'Ultra Rare', 'Secret Rare', 'Promo']
    },
    advanced: ["id", "code", "name", "type", "icons", "rarity", "difficulty", "control", "block", "block_zone", "attack", "vitality", "hand_size"]
  },
  "magic": {
    quickOptions: {
      colors:['W','U','B','R','G','C'],
      rarity:['common','uncommon','rare','mythic']
    },
    advanced: ["id","name","set","colorIdentity","type","supertypes","types","subtypes","rarity","layout","cmc","loyalty","gameFormat","legality","contains","language"]
  },
  "one-piece": {
    quickOptions: {
      type:  ['Character','Event','Stage','Leader'],
      color: ['Red','Green','Blue','Purple','Black','Yellow'],
      rarity:['C','UC','R','SR','SEC','L','P','SP']      
    },
    advanced: ["id","name","rarity","type","color","family","cost","power","counter","ability","trigger"]
  },
  "pokemon": { 
    quickOptions: {
      category:    ['Pokemon', 'Trainer', 'Energy'],
      pokemonType: ['Grass', 'Fire', 'Water', 'Lightning', 'Psychic', 'Fighting', 'Darkness', 'Metal', 'Colorless', 'Dragon', 'Fairy'],
      stage:       ['Basic', 'Stage 1', 'Stage 2', 'VSTAR', 'VMAX', 'Restored', 'BREAK', 'Mega'],
      rarity:      ['Common', 'Uncommon', 'Rare', 'Holo Rare', 'Double Rare', 'Ultra Rare', 'Illustration Rare', 'Special Illustration Rare', 'Secret Rare', 'Hyper Rare', 'Promo']
    }, 
    advanced: ["id", "code", "name", "category", "pokemonType", "stage", "rarity", "hp", "retreat", "illustrator", "weaknesses", "resistances"] 
  },
  "riftbound": { 
    quickOptions: {
      type:   ['Unit','Spell','Gear','Battlefield','Rune','Legend'],
      super:  ['Champion','Signature','Basic'],
      color:  ['Fury','Calm','Mind','Body','Chaos','Order'],
      rarity: ['Common','Uncommon','Rare','Epic','Showcase'],
    }, 
    advanced: ["id","name","number","code","rarity","super","type","color","energy","might","power"] 
  },
  "sorcery": {
    quickOptions: {
      type:   ['Avatar','Minion','Magic','Aura','Artifact','Site'],
      element:['Earth','Fire','Water','Air','None'],
      rarity: ['Unique','Elite','Exceptional','Ordinary'],
      finish: ['Standard','Foil'],
    },
    advanced: ["id","name","type","element","rarity","finish"]
  },
  "star-wars": { 
    quickOptions: { 
      type:  ['Base','Event','Force Token','Leader','Leader Unit','Token Unit','Token Upgrade','Unit'],
      arena: ['Ground','Space'],
      rarity:['Common','Uncommon','Rare','Special','Legendary']
    },
    advanced: ["id","type","cost","hp","power","rarity","frontText","backText","arenas","aspects","traits"]
  },
  "union-arena": { 
    quickOptions: {
      type:  ['Character','Site','Event','Action Point'],
      rarity:['C','U','R','R★','SR','SR★','SR★★']
    }, 
    advanced: ["id","code","name","type","rarity","ap","bp","affinity","effect","trigger"] 
  },
  "cyberpunk": {
    quickOptions: {
      card_type: ['Legend', 'Unit', 'Gear'],
      color:     ['Red', 'Blue', 'Green', 'White'],
      rarity:    ['Common', 'Uncommon', 'Rare', 'Legend', 'Iconic Legend']
    },
    advanced: ["id", "name", "color", "card_type", "rarity", "cost", "power", "ram"]
  },
  "yugioh": { 
    quickOptions: { 
      type:     ['Monster','Spell','Trap'],
      frameType:['xyz','synchro','fusion','link'],
      attribute:['Dark','Light','Fire','Water','Earth','Wind']
    },
    advanced: ["id","code","name","frameType","type","attribute","level","effect","race","atk","def","archetype"]
  },
  "hololive": {
    quickOptions: {
      type:   ['Oshi', 'holomem', 'Support', 'Cheer'],
      color:  ['Red', 'Blue', 'Green', 'White', 'Yellow', 'Purple', 'Neutral'],
      rarity: ['C', 'U', 'R', 'RR', 'OSR', 'OUR', 'UR', 'SEC', 'P']
    },
    advanced: ["id", "code", "name", "rarity", "type", "color", "bloomLevel", "hp", "life"]
  },
  "godzilla": {
    quickOptions: {
      type:   ['Monster', 'Battle', 'Strategy', 'Rage'],
      color:  ['Red', 'Blue', 'Green', 'White'],
      rarity: ['C', 'UC', 'R', 'SR', 'SP', 'SD', 'P']
    },
    advanced: ["id", "code", "name", "rarity", "type", "color", "grade", "threat", "counterPower"]
  },
  "altered": {
    quickOptions: {
      faction: ['Axiom', 'Bravos', 'Lyra', 'Muna', 'Ordis', 'Yzmir'],
      type:    ['Hero', 'Character', 'Spell', 'Permanent'],
      rarity:  ['Common', 'Rare', 'Unique']
    },
    advanced: ["id", "code", "name", "rarity", "type", "faction", "collector_number", "mainCost", "recallCost"]
  },
  "grand-archive": {
    quickOptions: {
      element: ['Norm', 'Fire', 'Water', 'Wind', 'Tera', 'Umbra', 'Luxem', 'Arcane'],
      type:    ['Champion', 'Ally', 'Action', 'Attack', 'Item', 'Weapon', 'Regalia'],
      class:   ['Assassin', 'Cleric', 'Guardian', 'Mage', 'Ranger', 'Tamer', 'Warrior']
    },
    advanced: ["id", "code", "name", "element", "type", "class", "level", "cost_memory", "cost_reserve", "rarity"]
  },
  "cardfight-vanguard": {
    quickOptions: {
      grade:  ['0', '1', '2', '3', '4'],
      type:   ['Normal Unit', 'Trigger Unit', 'G Unit', 'Normal Order', 'Blitz Order', 'Set Order'],
      nation: ['Dragon Empire', 'Dark States', 'Brandt Gate', 'Keter Sanctuary', 'Stoicheia', 'Lyrical Monasterio', 'Royal Paladin', 'Kagero', 'Shadow Paladin']
    },
    advanced: ["id", "code", "name", "grade", "type", "nation", "power", "shield", "rarity"]
  },
  "lorcana": {
    quickOptions: {
      color:  ['Amber', 'Amethyst', 'Emerald', 'Ruby', 'Sapphire', 'Steel'],
      type:   ['Character', 'Action', 'Item', 'Location', 'Action - Song'],
      rarity: ['Common', 'Uncommon', 'Rare', 'Super Rare', 'Legendary', 'Enchanted']
    },
    advanced: ["id", "code", "name", "color", "type", "rarity", "cost", "inkable", "lore", "strength", "willpower"]
  }
};

/**
 * Mapeamento de apelidos manuais conhecidos para a slug canônica
 */
const EXTRA_ALIASES: Record<string, string[]> = {
  "dragon-ball-fusion": ["dragon-ball"],
  "universus": ["uvs"],
  "grand-archive": ["grandarchive"],
  "cardfight-vanguard": ["vanguard"],
  "lorcana": ["disney-lorcana"],
  "pokemon": ["pokémon", "pokemon-tcg"]
};

/**
 * Monta o registro de configurações vinculando IDs numéricos e aliases à mesma referência,
 * garantindo sincronização sem duplicação no código ou memória.
 */
const buildFiltersDictionary = (): Record<string, GameFilterConfig> => {
  const dictionary: Record<string, GameFilterConfig> = { ...BASE_GAME_FILTERS };

  // 1. Vincula apelidos extras
  Object.entries(EXTRA_ALIASES).forEach(([slug, aliases]) => {
    const base = BASE_GAME_FILTERS[slug];
    if (base) {
      aliases.forEach(alias => {
        dictionary[alias.toLowerCase()] = base;
      });
    }
  });

  // 2. Vincula IDs e aliases do CARD_GAMES centralizado
  CARD_GAMES.forEach(game => {
    const base = BASE_GAME_FILTERS[game.slug] || dictionary[game.slug];
    if (base) {
      dictionary[String(game.id)] = base;
      dictionary[game.slug] = base;
      if (Array.isArray(game.aliases)) {
        game.aliases.forEach(alias => {
          const key = alias.toLowerCase().trim();
          if (!dictionary[key]) {
            dictionary[key] = base;
          }
        });
      }
    }
  });

  return dictionary;
};

const populatedFilters = buildFiltersDictionary();

/**
 * Objeto exportado com fallback inteligente via Proxy:
 * Se a chave requisitada for um ID numérico, slug ou qualquer alias,
 * resolve automaticamente para a configuração correta.
 */
export const GAME_FILTERS_CONFIG: Record<string, any> = new Proxy(populatedFilters, {
  get(target, prop: string | symbol) {
    if (typeof prop !== 'string') return Reflect.get(target, prop);
    if (prop in target) return target[prop];
    const canonicalSlug = getGameSlug(prop);
    if (canonicalSlug && target[canonicalSlug]) return target[canonicalSlug];
    const normalized = prop.toLowerCase().trim();
    return target[normalized] || undefined;
  }
});

export const pretty = (k: string) => ({
  id:'ID', name:'Nome', code:'Código', set:'Coleção', number:'Número',
  category:'Categoria', pokemonType:'Tipo', stage:'Estágio',
  cardType:'Tipo de Carta', color:'Cor', colors:'Cores',
  rarity:'Raridade', type:'Tipo', super:'Supertipo', element:'Elemento', finish:'Acabamento',
  energy:'Energia', might:'Poder', power:'Poder', domain:'Domínio',
  bloomLevel:'Nível de Bloom', hp:'HP', life:'Vida',
  grade:'Grau', threat:'Ameaça', counterPower:'Contra-Ataque',
  faction:'Facção', collector_number:'Número de Colecionador', mainCost:'Custo Principal', recallCost:'Custo de Retorno',
  class:'Classe', classes:'Classes', cost_memory:'Custo (Memória)', cost_reserve:'Custo (Reserva)', level:'Nível',
  talent:'Talento', talents:'Talentos', subtype:'Subtipo', subtypes:'Subtipos',
  nation:'Nação', clan:'Clã', shield:'Escudo',
  cost:'Custo', inkable:'Tintável', lore:'Lore', strength:'Força', willpower:'Vontade', classifications:'Classificações',
  ap:'AP', zone:'Zona', trait:'Característica', link:'Link', source_title:'Obra Original',
  icons:'Símbolo / Ícone', difficulty:'Dificuldade', control:'Controle', block:'Bloqueio', block_zone:'Zona de Bloqueio', vitality:'Vitalidade', hand_size:'Mão',
  tipo: 'Tipo', status: 'Status', formato: 'Formato',
  retreat: 'Recuo', illustrator: 'Ilustrador', weaknesses: 'Fraqueza', resistances: 'Resistência'
}[k] || k.charAt(0).toUpperCase() + k.slice(1));

export interface QuickFiltersProps {
  gameSlug?: string | number | null;
  activeFilters: Record<string, string>;
  onFilterChange?: (field: string, value: string | null) => void;
  onClearFilters?: () => void;
  setActiveFilters?: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  customQuickOptions?: Record<string, string[]>;
  title?: string;
  className?: string;
  defaultCollapsed?: boolean;
  prettyLabel?: (field: string) => string;
}

const getOptionDisplayLabel = (field: string, opt: string): string => {
  if (field === 'category') {
    if (opt === 'Pokemon') return 'Pokémon';
    if (opt === 'Trainer') return 'Treinador';
    if (opt === 'Energy') return 'Energia';
  }
  if (field === 'stage') {
    if (opt === 'Basic') return 'Básico';
    if (opt === 'Stage 1') return 'Estágio 1';
    if (opt === 'Stage 2') return 'Estágio 2';
    if (opt === 'Restored') return 'Restaurado';
  }
  return opt;
};

export const QuickFilters: React.FC<QuickFiltersProps> = ({
  gameSlug,
  activeFilters,
  onFilterChange,
  onClearFilters,
  setActiveFilters,
  customQuickOptions,
  title = 'Filtros Rápidos',
  className = '',
  defaultCollapsed = false,
  prettyLabel = pretty
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  // Normalize slug to retrieve quick options
  const quickFilterGroups = useMemo(() => {
    if (customQuickOptions) {
      return Object.entries(customQuickOptions) as [string, string[]][];
    }

    if (!gameSlug) return [];
    const canonicalSlug = getGameSlug(gameSlug);
    const config = GAME_FILTERS_CONFIG[canonicalSlug] || GAME_FILTERS_CONFIG[String(gameSlug)];
    if (!config?.quickOptions) return [];

    // Adaptação dinâmica para Pokémon conforme a Categoria selecionada
    if (canonicalSlug === 'pokemon') {
      const selectedCategory = activeFilters.category;
      const opts: Record<string, string[]> = {
        category: ['Pokemon', 'Trainer', 'Energy']
      };

      if (selectedCategory === 'Trainer') {
        opts.pokemonType = ['Supporter', 'Item', 'Stadium', 'Tool'];
        opts.rarity = [
          'Common', 'Uncommon', 'Rare', 'Holo Rare', 'Ultra Rare',
          'Illustration Rare', 'Special Illustration Rare', 'Secret Rare', 'Hyper Rare', 'ACE SPEC Rare', 'Promo'
        ];
      } else if (selectedCategory === 'Energy') {
        opts.pokemonType = ['Basic Energy', 'Special Energy'];
        opts.rarity = ['Common', 'Uncommon', 'Rare', 'Holo Rare', 'Secret Rare', 'Hyper Rare', 'Promo'];
      } else {
        // Categoria 'Pokemon' ou geral
        opts.pokemonType = [
          'Grass', 'Fire', 'Water', 'Lightning', 'Psychic', 'Fighting',
          'Darkness', 'Metal', 'Colorless', 'Dragon', 'Fairy'
        ];
        opts.stage = [
          'Basic', 'Stage 1', 'Stage 2', 'VSTAR', 'VMAX', 'Restored', 'BREAK', 'Mega'
        ];
        opts.rarity = [
          'Common', 'Uncommon', 'Rare', 'Holo Rare', 'Double Rare', 'Ultra Rare',
          'Illustration Rare', 'Special Illustration Rare', 'Secret Rare', 'Hyper Rare', 'Promo'
        ];
      }
      return Object.entries(opts) as [string, string[]][];
    }

    return Object.entries(config.quickOptions) as [string, string[]][];
  }, [gameSlug, customQuickOptions, activeFilters.category]);

  // Retorna os valores atualmente selecionados para um campo e se está no modo AND (__all)
  const getFieldState = (field: string) => {
    const rawAll = activeFilters[`${field}__all`];
    const rawOr = activeFilters[field];

    if (rawAll !== undefined && rawAll !== null && rawAll.trim() !== '') {
      const values = rawAll.split(',').map(s => s.trim()).filter(Boolean);
      return { values, isAll: true };
    }
    if (rawOr !== undefined && rawOr !== null && rawOr.trim() !== '') {
      const values = rawOr.split(',').map(s => s.trim()).filter(Boolean);
      return { values, isAll: false };
    }
    return { values: [], isAll: false };
  };

  const activeCount = useMemo(() => {
    if (!quickFilterGroups.length) return 0;
    return quickFilterGroups.filter(([field]) => {
      const { values } = getFieldState(field);
      return values.length > 0;
    }).length;
  }, [quickFilterGroups, activeFilters]);

  const hasAnyFilterActive = useMemo(() => {
    return Object.values(activeFilters).some(v => Boolean(v && v.trim()));
  }, [activeFilters]);

  const handleToggleOption = (field: string, opt: string) => {
    const { values, isAll } = getFieldState(field);
    const isSelected = values.includes(opt);

    let nextValues: string[];
    if (isSelected) {
      nextValues = values.filter(v => v !== opt);
    } else {
      nextValues = [...values, opt];
    }

    if (setActiveFilters) {
      setActiveFilters(prev => {
        const updated = { ...prev };
        delete updated[field];
        delete updated[`${field}__all`];

        if (nextValues.length > 0) {
          // Se estava em modo AND e ainda tem mais de 1 valor, mantém AND (__all); caso contrário usa OR (padrão)
          const keyToUse = isAll && nextValues.length > 1 ? `${field}__all` : field;
          updated[keyToUse] = nextValues.join(',');
        }

        // Se alternar a Categoria no Pokémon, limpa o tipo e o estágio para não manter filtros inválidos
        if (field === 'category') {
          if (getGameSlug(gameSlug) === 'pokemon') {
            delete updated.pokemonType;
            delete updated.stage;
            delete updated.pokemonType__all;
            delete updated.stage__all;
          }
        }
        return updated;
      });
    }

    if (onFilterChange) {
      if (nextValues.length > 0) {
        const keyToUse = isAll && nextValues.length > 1 ? `${field}__all` : field;
        onFilterChange(keyToUse, nextValues.join(','));
      } else {
        onFilterChange(field, null);
      }
    }
  };

  const handleToggleMode = (field: string) => {
    const { values, isAll } = getFieldState(field);
    if (values.length < 2) return;

    if (setActiveFilters) {
      setActiveFilters(prev => {
        const updated = { ...prev };
        delete updated[field];
        delete updated[`${field}__all`];

        if (isAll) {
          // Muda para OR (padrão)
          updated[field] = values.join(',');
        } else {
          // Muda para AND (__all)
          updated[`${field}__all`] = values.join(',');
        }
        return updated;
      });
    }
  };

  const handleClear = () => {
    if (setActiveFilters) {
      setActiveFilters({});
    }
    if (onClearFilters) {
      onClearFilters();
    }
  };

  if (quickFilterGroups.length === 0) {
    return null;
  }

  return (
    <div 
      className={`bg-slate-900/60 border border-slate-800/80 rounded-2xl p-2 sm:px-4 sm:py-3 shadow-md transition-all ${className}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center space-x-2">
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
            {title}
          </span>
          {activeCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-md bg-purple-600/30 text-purple-300 border border-purple-500/40 text-[9px] font-black">
              {activeCount} ativo{activeCount > 1 ? 's' : ''}
            </span>
          )}
        </div>

        <div className="flex items-center space-x-3">
          {hasAnyFilterActive && (
            <button
              type="button"
              onClick={handleClear}
              className="text-[10px] font-bold text-slate-400 hover:text-white uppercase tracking-wider flex items-center space-x-1 transition-colors cursor-pointer"
              title="Limpar todos os filtros ativos"
            >
              <i className="fas fa-trash-can text-[9px]"></i>
              <span className="hidden xs:inline">Limpar</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => setIsCollapsed(prev => !prev)}
            className="text-[10px] font-bold text-slate-400 hover:text-white flex items-center space-x-1.5 transition-colors py-0.5 px-2 rounded-lg hover:bg-slate-800 cursor-pointer"
            title={isCollapsed ? "Mostrar filtros rápidos" : "Recolher filtros rápidos"}
          >
            <span>{isCollapsed ? "Mostrar" : "Recolher"}</span>
            <i className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'} text-[8px]`}></i>
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div className="space-y-2 border-slate-800/40 mt-2.5">
          {quickFilterGroups.map(([field, options]) => {
            const { values, isAll } = getFieldState(field);
            return (
              <div key={field} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs">
                {/* Rótulo do Campo + Botão Alternador OU (OR) / E (AND - __all) */}
                <div className="flex items-center space-x-1.5 w-auto sm:w-28 shrink-0">
                  <span className="text-[10px] font-bold text-slate-400 capitalize truncate">
                    {prettyLabel(field)}:
                  </span>
                  {values.length >= 2 && (
                    <button
                      type="button"
                      onClick={() => handleToggleMode(field)}
                      className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider transition-all flex items-center space-x-1 border cursor-pointer active:scale-95 ${
                        isAll
                          ? 'bg-purple-600 text-white border-purple-400 shadow-sm shadow-purple-600/30'
                          : 'bg-slate-800 text-slate-300 border-slate-700 hover:text-white'
                      }`}
                      title={
                        isAll
                          ? "Modo AND (__all) ativo: exige que a carta contenha TODOS os valores selecionados. Clique para alternar para OU (qualquer valor)."
                          : "Modo OR (padrão) ativo: a carta pode ter QUALQUER um dos valores selecionados. Clique para alternar para E (todos os valores)."
                      }
                    >
                      <i className={`fas ${isAll ? 'fa-link' : 'fa-code-fork'} text-[7px]`}></i>
                      <span>{isAll ? 'E (AND)' : 'OU (OR)'}</span>
                    </button>
                  )}
                </div>

                {/* Opções Clicáveis com Suporte a Múltipla Seleção */}
                <div className="flex items-center gap-1 overflow-x-auto no-scrollbar py-0.5 flex-nowrap sm:flex-wrap">
                  {options.map((opt: string) => {
                    const isSelected = values.includes(opt);
                    const displayLabel = getOptionDisplayLabel(field, opt);

                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => handleToggleOption(field, opt)}
                        className={`px-2 py-0.5 rounded-lg border text-[10px] font-medium shrink-0 transition-all cursor-pointer flex items-center space-x-1 active:scale-95 ${
                          isSelected
                            ? 'bg-purple-600 border-purple-500 text-white font-bold shadow-sm shadow-purple-600/20'
                            : 'bg-slate-950/70 border-slate-800/80 text-slate-400 hover:text-slate-200 hover:border-slate-700'
                        }`}
                        title={isSelected ? `Remover filtro ${opt}` : `Adicionar filtro ${opt}`}
                      >
                        <span>{displayLabel}</span>
                        {isSelected && (
                          <i className="fas fa-check text-[8px] text-white/80 ml-0.5"></i>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
